import { useEffect, useState } from 'react';
import type { ApiClient, UnterlagenGewerk } from './api';

/**
 * Der Knopf „Unterlagen als ZIP" (Auftrag Fachplaner 16.09.2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DER KNOPF DEN DATEINAMEN NICHT SETZT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Er kommt aus `Content-Disposition` — `8000_LUE_Unterlagen_2026-09-16.zip`.
 * Der Server kennt Projektnummer, Gewerkekürzel und Stand; der Browser
 * kennt sie nicht. Das erledigt `dateiLaden` über `dateinameAusAntwort`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE ZAHL AM KNOPF STEHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Lüftungspaket sind rund 194 frisch zu erzeugende Berichte. Ohne die
 * Zahl sieht ein Klick aus wie jeder andere, und wer nach zwei Minuten
 * nichts sieht, klickt ein zweites Mal — und startet die Erzeugung noch
 * einmal. Die Zahl kommt aus `GET unterlagen`, das NICHTS erzeugt.
 */
export function UnterlagenZipKnopf({
  laden, arten, dateien, beschriftung, hinweis,
}: {
  /** Lädt das Paket. `art` leer = alles. */
  laden: (art?: string) => Promise<void>;
  /** Die Werte, die `?art=` annimmt. Leer = keine Aufteilung anbieten. */
  arten?: string[];
  /** Wie viele Dateien ohne Filter entstünden. */
  dateien?: number;
  beschriftung?: string;
  hinweis?: string;
}) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);

  const hole = async (art?: string) => {
    setLaeuft(art ?? '*');
    setFehler(null);
    try {
      await laden(art);
      setOffen(false);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const vielDateien = (dateien ?? 0) > 60;

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <button type="button" className="knopf" disabled={laeuft !== null}
        onClick={() => hole(undefined)}>
        {laeuft === '*'
          ? 'packt …'
          : `${beschriftung ?? 'Unterlagen als ZIP'}${
            dateien !== undefined ? ` (${dateien})` : ''}`}
      </button>

      {/* Die Aufteilung nach Art ist der Ausweg, wenn das Gesamtpaket zu
          lange dauert. Sie erscheint erst, wenn es etwas aufzuteilen gibt -
          bei zwei Dateien waere sie nur ein Knopf mehr. */}
      {arten && arten.length > 1 && vielDateien && (
        <button type="button" className="knopf" disabled={laeuft !== null}
          onClick={() => setOffen((o) => !o)}>
          {offen ? 'Teilpakete ausblenden' : 'Teilpakete …'}
        </button>
      )}

      {offen && arten?.map((a) => (
        <button key={a} type="button" className="knopf" disabled={laeuft !== null}
          onClick={() => hole(a)}>
          {laeuft === a ? 'packt …' : a}
        </button>
      ))}

      {hinweis && !fehler && (
        <span className="schwach" style={{ fontSize: 11 }}>{hinweis}</span>
      )}

      {fehler && <span className="hinweis fehler" style={{ fontSize: 11 }}>{fehler}</span>}
    </span>
  );
}

/**
 * Derselbe Knopf für ein Gewerk mit Unterlagenpaket — holt sich selbst, was
 * darin läge.
 *
 * Schlägt die Übersicht fehl (kein Projekt, keine Rechte), bleibt der Knopf
 * trotzdem stehen: Ein Paket ohne Vorschau ist immer noch ein Paket, und
 * eine Seite, an der der Knopf mal da ist und mal nicht, verwirrt mehr als
 * eine fehlende Zahl.
 */
export function GewerkUnterlagenZip({
  api, projektId, gewerk, beschriftung,
}: {
  api: ApiClient;
  projektId: string;
  gewerk: string;
  beschriftung?: string;
}) {
  const [zeile, setZeile] = useState<UnterlagenGewerk | null>(null);

  useEffect(() => {
    let lebt = true;

    api.unterlagenUebersicht(projektId)
      .then((zeilen) => {
        if (lebt) setZeile(zeilen.find((z) => z.gewerk === gewerk) ?? null);
      })
      .catch(() => { if (lebt) setZeile(null); });

    return () => { lebt = false; };
  }, [api, projektId, gewerk]);

  return (
    <UnterlagenZipKnopf
      laden={(art) => api.unterlagenZip(projektId, gewerk, art)}
      arten={zeile?.arten}
      dateien={zeile?.dateien}
      beschriftung={beschriftung}
      hinweis={zeile && zeile.dateien > 60
        ? 'wird beim Abruf frisch erzeugt — das dauert'
        : undefined}
    />
  );
}
